import { useState } from "react";
import { ChevronDown, Plus, Ruler, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  getTemplateMeasurementFields,
  type TemplateMeasurementValues,
  type TemplateOpening,
} from "@/lib/template-measurements";

const KINDS: { value: TemplateOpening["kind"]; label: string }[] = [
  { value: "window", label: "Window" },
  { value: "door", label: "Door" },
  { value: "rooflight", label: "Rooflight" },
];

let seq = 0;
const newId = () => `opening-${Date.now().toString(36)}-${++seq}`;

export function TemplateMeasurements({ projectType, values, onChange, openings, onOpeningsChange }: {
  projectType: string;
  values: TemplateMeasurementValues;
  onChange: (values: TemplateMeasurementValues) => void;
  openings: TemplateOpening[];
  onOpeningsChange: (rows: TemplateOpening[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const fields = getTemplateMeasurementFields(projectType);
  const provided = fields.filter(f => (values[f.id] ?? "").trim() !== "").length;

  const setValue = (id: string, v: string) => {
    const next = { ...values };
    if (v === "") delete next[id]; else next[id] = v;
    onChange(next);
  };
  const updateRow = (id: string, patch: Partial<TemplateOpening>) =>
    onOpeningsChange(openings.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = (kind: TemplateOpening["kind"]) =>
    onOpeningsChange([...openings, { id: newId(), kind, width: "", height: "", quantity: "", specification: "" }]);
  const removeRow = (id: string) => onOpeningsChange(openings.filter(r => r.id !== id));

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border" data-testid="template-measurements">
      <CollapsibleTrigger asChild>
        <button type="button" className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium hover:bg-muted/30" data-testid="button-toggle-template-measurements">
          <Ruler className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1">Precise measurements (optional)</span>
          <span className="text-xs text-muted-foreground">
            {provided + openings.length > 0 ? `${provided} measured, ${openings.length} openings` : "None entered"}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-5 border-t p-3">
        <p className="text-xs text-muted-foreground">Leave a field blank if you have not measured it. Blank fields are not used and nothing is assumed in their place.</p>
        {fields.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(f => {
              const inputId = `tm-${f.id}`;
              return (
                <div key={f.id} className="space-y-1">
                  <Label htmlFor={inputId} className="text-xs">
                    {f.label}{f.unit && <span className="text-muted-foreground"> ({f.unit})</span>}
                  </Label>
                  <Input
                    id={inputId}
                    data-testid={`input-measurement-${f.id}`}
                    type={f.type === "text" ? "text" : "number"}
                    inputMode={f.type === "text" ? "text" : "decimal"}
                    min={f.type === "text" ? undefined : 0}
                    step="any"
                    className="h-9"
                    value={values[f.id] ?? ""}
                    onChange={e => setValue(f.id, e.target.value)}
                  />
                  {f.hint && <p className="text-[11px] leading-snug text-muted-foreground">{f.hint}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No precise measurement fields for this project type.</p>
        )}

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">Windows, doors and rooflights</p>
            <div className="flex flex-wrap gap-1.5">
              {KINDS.map(k => (
                <Button key={k.value} type="button" size="sm" variant="outline" onClick={() => addRow(k.value)} data-testid={`button-add-opening-${k.value}`}>
                  <Plus className="mr-1 h-3.5 w-3.5" />{k.label}
                </Button>
              ))}
            </div>
          </div>
          {openings.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="text-no-openings">No openings added. Opening sizes are not inferred when left blank.</p>
          ) : (
            <div className="space-y-2">
              {openings.map((r, i) => (
                <div key={r.id} className="grid grid-cols-2 items-end gap-2 rounded-md border p-2 sm:grid-cols-[7rem_1fr_1fr_5rem_2fr_auto]" data-testid={`row-opening-${i}`}>
                  <div className="space-y-1">
                    <Label htmlFor={`op-kind-${r.id}`} className="text-xs">Type</Label>
                    <select id={`op-kind-${r.id}`} className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={r.kind}
                      onChange={e => updateRow(r.id, { kind: e.target.value as TemplateOpening["kind"] })}>
                      {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                    </select>
                  </div>
                  {([["width", "Width (mm)"], ["height", "Height (mm)"], ["quantity", "Qty"]] as const).map(([key, label]) => (
                    <div key={key} className="space-y-1">
                      <Label htmlFor={`op-${key}-${r.id}`} className="text-xs">{label}</Label>
                      <Input id={`op-${key}-${r.id}`} type="number" inputMode="decimal" min={0} step="any" className="h-9"
                        data-testid={`input-opening-${key}-${i}`} value={r[key]} onChange={e => updateRow(r.id, { [key]: e.target.value })} />
                    </div>
                  ))}
                  <div className="col-span-2 space-y-1 sm:col-span-1">
                    <Label htmlFor={`op-spec-${r.id}`} className="text-xs">Specification</Label>
                    <Input id={`op-spec-${r.id}`} className="h-9" data-testid={`input-opening-specification-${i}`} value={r.specification}
                      onChange={e => updateRow(r.id, { specification: e.target.value })} />
                  </div>
                  {r.kind !== "rooflight" && <label className="col-span-2 flex items-center gap-1.5 text-xs text-muted-foreground sm:col-span-full sm:order-last">
                    <input type="checkbox" checked={!!r.deductFromWalls} onChange={e => updateRow(r.id, { deductFromWalls: e.target.checked })}
                      data-testid={`toggle-opening-deduction-${i}`} />
                    Deduct this opening from the measured wall area
                  </label>}
                  <Button type="button" size="icon" variant="ghost" className="h-9 w-9" aria-label={`Remove opening ${i + 1}`}
                    onClick={() => removeRow(r.id)} data-testid={`button-remove-opening-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Measurements refine matching work items; they do not add work that is outside the selected scope. Bundled services remain allowances unless replaced with per-point or per-metre library items. Every measured line also has its own quantity calculator.</p>
      </CollapsibleContent>
    </Collapsible>
  );
}
